import { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  isFetchBaseQueryError,
  isGraphQLError,
  isGraphQLErrorArray,
  isGraphQLErrorWithDebugObject,
  isSerializedError,
} from 'error/typeGuards';
import { ApiErrorResponse, DebugMessage } from 'error/types';
import { GraphQLError } from 'graphql';
import i18n from 'locales/i18n';
import { translations } from 'locales/translations';

export type MaybeError = FetchBaseQueryError | GraphQLError | GraphQLError[] | SerializedError | undefined;

export function getErrorMessage(error: MaybeError, defaultMessage?: string): string | string[] {
  if (!error) {
    return 'Unknown error occurred.';
  }

  if (isFetchBaseQueryError(error)) {
    if ('error' in error) {
      return `${error.status}: ${error.error}`;
    }

    const errData = error.data;

    if (typeof errData === 'string') {
      return errData;
    }

    if (typeof errData === 'object' && errData !== null) {
      const apiError = errData as ApiErrorResponse;

      if (apiError.message) {
        return apiError.message;
      }
      if (apiError.error) {
        return apiError.error;
      }
      if (Array.isArray(apiError.errors)) {
        return apiError.errors;
      }
    }

    return defaultMessage || `Unexpected API error format`;
  }

  // this typeguard must preceed isSerializedError!
  if (isGraphQLError(error)) {
    return processGraphQLError(error);
  }

  if (isGraphQLErrorArray(error)) {
    return error.map(e => processGraphQLError(e));
  }

  if (isSerializedError(error)) {
    return error.message ?? (defaultMessage || 'Unexpected client error');
  }

  return 'Unhandled error type';
}

function processGraphQLError(error: GraphQLError): string {
  const { debugMessage } = error.extensions;
  let errorMessage: string;
  if (isGraphQLErrorWithDebugObject(error)) {
    errorMessage = (debugMessage as DebugMessage).message;
  }

  errorMessage = debugMessage as string;

  switch (errorMessage) {
    case 'Asset not found':
      return i18n.t(translations.commonMessages.assetNotFound, 'Asset not found') as unknown as string;
    default:
      return errorMessage;
  }
}
